import type { ConnectorOAuthLocalIntent } from "@/models/connectorOAuth";
import { isValidConnectorOAuthReturnPath } from "@/lib/oauth/connectorOAuthProtocol";

const STORAGE_PREFIX = "cns:connector-oauth-intent:v1:";
const INTENT_TTL_MS = 30 * 60 * 1000;

function storageKey(attemptPublicId: string): string {
  return `${STORAGE_PREFIX}${attemptPublicId}`;
}

function readIntent(raw: string | null): ConnectorOAuthLocalIntent | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<ConnectorOAuthLocalIntent>;
    if (
      !value.attemptPublicId ||
      !value.credentialPublicId ||
      !value.surface ||
      typeof value.createdAt !== "number" ||
      Date.now() - value.createdAt > INTENT_TTL_MS
    ) {
      return null;
    }
    if (value.returnPath && !isValidConnectorOAuthReturnPath(value.returnPath)) {
      return null;
    }
    return value as ConnectorOAuthLocalIntent;
  } catch {
    return null;
  }
}

/**
 * Session-local recovery metadata keyed by attempt id.
 *
 * The index never determines OAuth success or failure; attempt endpoints remain authoritative.
 */
export class ConnectorOAuthIntentIndex {
  save(intent: ConnectorOAuthLocalIntent): void {
    if (typeof sessionStorage === "undefined") return;
    if (intent.returnPath && !isValidConnectorOAuthReturnPath(intent.returnPath)) return;
    try {
      sessionStorage.setItem(storageKey(intent.attemptPublicId), JSON.stringify(intent));
    } catch {
      // Storage can be disabled; the backend attempt still carries durable correlation.
    }
  }

  get(attemptPublicId: string): ConnectorOAuthLocalIntent | null {
    if (typeof sessionStorage === "undefined") return null;
    try {
      const key = storageKey(attemptPublicId);
      const intent = readIntent(sessionStorage.getItem(key));
      if (!intent) sessionStorage.removeItem(key);
      return intent;
    } catch {
      return null;
    }
  }

  remove(attemptPublicId: string): void {
    if (typeof sessionStorage === "undefined") return;
    try {
      sessionStorage.removeItem(storageKey(attemptPublicId));
    } catch {
      // Storage cleanup is best effort.
    }
  }

  /** Removes all session-local OAuth intents at an authentication boundary. */
  clear(): void {
    if (typeof sessionStorage === "undefined") return;
    try {
      const keys: string[] = [];
      for (let index = 0; index < sessionStorage.length; index += 1) {
        const key = sessionStorage.key(index);
        if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
      }
      for (const key of keys) sessionStorage.removeItem(key);
    } catch {
      // Storage cleanup is best effort.
    }
  }
}
